import { useCallback, useRef, useEffect, useState } from "react"
import type { ChatPosition } from "../stores/chat-store"

const DRAG_THRESHOLD = 5 // pixels of movement before considered a drag

interface UseDragOptions {
  position: ChatPosition
  onPositionChange: (position: ChatPosition) => void
  onVisualUpdate?: (position: ChatPosition) => void // Direct DOM updates during drag (bypasses React)
  onDragStart?: () => void
  onDragEnd?: () => void
  onClick?: () => void // Called when it's a click, not a drag
  minEdgePadding?: number
  widgetWidth: number
  widgetHeight: number
  disabled?: boolean
}

interface UseDragReturn {
  handleMouseDown: (e: React.MouseEvent) => void
  handleTouchStart: (e: React.TouchEvent) => void
  isPointerDown: boolean // True immediately on mousedown (before drag threshold)
}

export function useDrag({
  position,
  onPositionChange,
  onVisualUpdate,
  onDragStart,
  onDragEnd,
  onClick,
  minEdgePadding = 20,
  widgetWidth,
  widgetHeight,
  disabled = false,
}: UseDragOptions): UseDragReturn {
  const isDraggingRef = useRef(false)
  const hasDraggedRef = useRef(false)
  const [isPointerDown, setIsPointerDown] = useState(false)

  // RAF batching refs - only update visual position once per frame
  const rafIdRef = useRef<number | null>(null)
  const pendingPositionRef = useRef<ChatPosition | null>(null)
  const lastVisualPositionRef = useRef<ChatPosition>(position)

  // Store current values in refs so event handlers always have latest values
  const positionRef = useRef(position)
  const optionsRef = useRef({
    onPositionChange,
    onVisualUpdate,
    onDragStart,
    onDragEnd,
    onClick,
    minEdgePadding,
    widgetWidth,
    widgetHeight,
    disabled,
  })

  // Keep refs updated
  useEffect(() => {
    positionRef.current = position
    lastVisualPositionRef.current = position
  }, [position])

  useEffect(() => {
    optionsRef.current = {
      onPositionChange,
      onVisualUpdate,
      onDragStart,
      onDragEnd,
      onClick,
      minEdgePadding,
      widgetWidth,
      widgetHeight,
      disabled,
    }
  }, [onPositionChange, onVisualUpdate, onDragStart, onDragEnd, onClick, minEdgePadding, widgetWidth, widgetHeight, disabled])

  // Store drag start position
  const startRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 })

  // Clamp position to viewport bounds
  const clampPosition = useCallback((x: number, y: number): ChatPosition => {
    const { widgetWidth: w, widgetHeight: h, minEdgePadding: padding } = optionsRef.current
    const maxX = window.innerWidth - w - padding
    const maxY = window.innerHeight - h - padding

    return {
      x: Math.max(padding, Math.min(x, maxX)),
      y: Math.max(padding, Math.min(y, maxY)),
    }
  }, [])

  // Mouse/touch move handler - uses RAF batching for performance
  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDraggingRef.current) return

    const deltaX = startRef.current.x - clientX
    const deltaY = startRef.current.y - clientY

    // Check if we've moved past the threshold
    if (!hasDraggedRef.current) {
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      if (distance < DRAG_THRESHOLD) return
      hasDraggedRef.current = true
      optionsRef.current.onDragStart?.()
    }

    // Calculate new position (x/y are from right/bottom edges)
    const newX = startRef.current.posX + deltaX
    const newY = startRef.current.posY + deltaY

    const clamped = clampPosition(newX, newY)

    // Store pending position and track last visual position
    pendingPositionRef.current = clamped
    lastVisualPositionRef.current = clamped

    // Schedule RAF if not already pending - batches updates to 60fps max
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null
        if (pendingPositionRef.current) {
          // Call visual update for direct DOM manipulation (bypasses React)
          optionsRef.current.onVisualUpdate?.(pendingPositionRef.current)
          pendingPositionRef.current = null
        }
      })
    }
  }, [clampPosition])

  // End drag handler
  const handleEnd = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false
      setIsPointerDown(false)

      // Cancel any pending RAF
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }

      if (hasDraggedRef.current) {
        // Persist final position to Zustand (only once at end of drag)
        optionsRef.current.onPositionChange(lastVisualPositionRef.current)
        optionsRef.current.onDragEnd?.()
      } else {
        // It was a click (no movement past threshold)
        optionsRef.current.onClick?.()
      }

      // Re-enable text selection
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
    }
  }, [])

  // Stable event handlers that use the callbacks above
  const mouseMoveHandler = useCallback((e: MouseEvent) => {
    handleMove(e.clientX, e.clientY)
  }, [handleMove])

  const mouseUpHandler = useCallback(() => {
    handleEnd()
    document.removeEventListener("mousemove", mouseMoveHandler)
    document.removeEventListener("mouseup", mouseUpHandler)
  }, [handleEnd, handleMove]) // eslint-disable-line react-hooks/exhaustive-deps

  const touchMoveHandler = useCallback((e: TouchEvent) => {
    if (!e.touches[0]) return
    e.preventDefault() // Prevent scrolling while dragging
    handleMove(e.touches[0].clientX, e.touches[0].clientY)
  }, [handleMove])

  const touchEndHandler = useCallback(() => {
    handleEnd()
    document.removeEventListener("touchmove", touchMoveHandler)
    document.removeEventListener("touchend", touchEndHandler)
  }, [handleEnd, handleMove]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mouse down handler - start drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (optionsRef.current.disabled || e.button !== 0) return // Only left mouse button

    e.preventDefault()
    isDraggingRef.current = true
    hasDraggedRef.current = false
    setIsPointerDown(true) // Immediately set to disable transitions

    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: positionRef.current.x,
      posY: positionRef.current.y,
    }

    // Prevent text selection during drag
    document.body.style.userSelect = "none"
    document.body.style.cursor = "grabbing"

    document.addEventListener("mousemove", mouseMoveHandler)
    document.addEventListener("mouseup", mouseUpHandler)
  }, [mouseMoveHandler, mouseUpHandler])

  // Touch start handler
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (optionsRef.current.disabled || !e.touches[0]) return

    const touch = e.touches[0]
    isDraggingRef.current = true
    hasDraggedRef.current = false
    setIsPointerDown(true) // Immediately set to disable transitions

    startRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      posX: positionRef.current.x,
      posY: positionRef.current.y,
    }

    document.addEventListener("touchmove", touchMoveHandler, { passive: false })
    document.addEventListener("touchend", touchEndHandler)
  }, [touchMoveHandler, touchEndHandler])

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      // Cancel any pending RAF
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }
      document.removeEventListener("mousemove", mouseMoveHandler)
      document.removeEventListener("mouseup", mouseUpHandler)
      document.removeEventListener("touchmove", touchMoveHandler)
      document.removeEventListener("touchend", touchEndHandler)
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    handleMouseDown,
    handleTouchStart,
    isPointerDown,
  }
}

// Hook to keep widget in viewport on window resize
export function useViewportClamp(
  position: ChatPosition,
  onPositionChange: (position: ChatPosition) => void,
  widgetWidth: number,
  widgetHeight: number,
  minEdgePadding: number = 20
) {
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - widgetWidth - minEdgePadding
      const maxY = window.innerHeight - widgetHeight - minEdgePadding

      const clampedX = Math.max(minEdgePadding, Math.min(position.x, maxX))
      const clampedY = Math.max(minEdgePadding, Math.min(position.y, maxY))

      if (clampedX !== position.x || clampedY !== position.y) {
        onPositionChange({ x: clampedX, y: clampedY })
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [position, onPositionChange, widgetWidth, widgetHeight, minEdgePadding])
}
